// @flow
import queryString from 'query-string';
import { getJson, putJson, postJson, del } from 'utils/http';
import type { IdealistDayEvent as GroupIdealistDayEvent } from 'api/groupAdmin';
import type { OrgMini, ListingMini } from 'api/orgs';
import type { ApplicantProfile } from 'api/ats';

export type EventStatus =
  | 'APPROVED'
  | 'PENDING'
  | 'REJECTED'
  | 'ARCHIVED'
  | 'DELETED'
  | 'DRAFT'
  | 'FROZEN'
  | 'HIDDEN'
  | 'PUBLISHED'
  | 'PULLED';

export type IdealistDayEvent = {
  ...$Exact<GroupIdealistDayEvent>,
  status: $ReadOnlyArray<EventStatus>,
  isApproved: ?boolean
};

export type Image = {
  filename: string,
  handle: string,
  mimetype: string,
  size: number,
  source: string
};

export type UserInfo = {
  id: string,
  created: number,
  updated: number,
  firstName: ?string,
  lastName: ?string,
  email: ?string,
  emailVerified: boolean,
  hasPassword: boolean,
  locale: string,
  hasPaymentInfo: boolean,
  locationText: string,
  stateCode: string,
  countryCode: string,
  latitude: number,
  longitude: number,
  timezone: string,
  profile: ?{
    description: string,
    links: Array<string>,
    image: ?Image
  },
  isLastOwnerOfOrgs: Array<{
    id: string,
    name: string,
    hasOutstandingInvoices: boolean,
    credits: number
  }>,
  isLastOwnerOfGroups: Array<{
    id: string,
    name: string
  }>,
  hasEvents: boolean,
  canExportData: boolean
};

export type SavedListing = {
  id: string,
  type: string,
  listing: {
    id: string,
    name: string,
    description: string,
    addressCity: string,
    addressState: string,
    addressCountry: string,
    url: { [string]: string }
  }
};

export type SavedSearch = {
  id: string,
  text: string,
  created: string,
  filters: Object,
  location: {
    location: ?string,
    hasGeo: boolean,
    geoType: ?string,
    stateCode: ?string,
    countryCode: ?string,
    latitude: ?number,
    longitude: ?number,
    boundingBoxN: ?number,
    boundingBoxS: ?number,
    boundingBoxW: ?number,
    boundingBoxE: ?number
  },
  radius: number
};

export type ListingApplication = {
  created: string,
  id: string,
  listing: ListingMini,
  org: OrgMini
};

export type GeneralUserInfo = {
  user: UserInfo,
  savedListings: Array<SavedListing>,
  savedSearches: Array<SavedSearch>,
  authProviders: Array<string>,
  idealistDayEvents: Array<IdealistDayEvent>
};

export type NotificationPreferences = {
  updatedByUser?: ?number,
  receiveEmails: ?boolean,
  receiveEmailsExplicitlyChosen: ?boolean
};

export type PaymentMethod = {
  id: string,
  brand: string,
  last4: string,
  name: string,
  type: string,
  onPendingPayment: false
};

export type ApplicantProfilePayload = {
  resumeS3FileId: string,
  applicantProfile?: ApplicantProfile,
  isRecruitable: ?boolean
};

export function fetchEvent(eventId: string): Promise<IdealistDayEvent> {
  return getJson(`/data/userdashboard/idealist-day-events/${eventId}`).then(
    ({ idealistDayEvent: event }) => event
  );
}

export function createEvent(eventInfo: Object): Promise<IdealistDayEvent> {
  return postJson(`/data/userdashboard/idealist-day-events`, eventInfo).then(
    ({ idealistDayEvent }) => idealistDayEvent
  );
}

export function updateEvent(
  eventId: string,
  eventInfo: Object
): Promise<IdealistDayEvent> {
  return putJson(
    `/data/userdashboard/idealist-day-events/${eventId}`,
    eventInfo
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function publishEvent(eventId: string) {
  return postJson(
    `/data/userdashboard/idealist-day-events/${eventId}/post`
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function hideEvent(eventId: string) {
  return postJson(
    `/data/userdashboard/idealist-day-events/${eventId}/hide`
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function unhideEvent(eventId: string) {
  return postJson(
    `/data/userdashboard/idealist-day-events/${eventId}/unhide`
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function deleteEvent(eventId: string) {
  return del(`/data/userdashboard/idealist-day-events/${eventId}`).then(
    ({ idealistDayEvent }) => idealistDayEvent
  );
}

export function fetchUserEvents(): Promise<Array<IdealistDayEvent>> {
  return getJson(`/data/userdashboard/idealist-day-events`).then(
    ({ events }) => events
  );
}

export function getGeneralUserInfo(): Promise<GeneralUserInfo> {
  return getJson('/data/userdashboard/general-settings').then(data => ({
    user: {
      id: data.id,
      created: data.created,
      updated: data.updated,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      emailVerified: data.emailVerified,
      hasPassword: data.hasPassword,
      locale: data.locale,
      hasPaymentInfo: data.hasPaymentInfo,
      stateCode: data.stateCode,
      countryCode: data.countryCode,
      latitude: data.latitude,
      longitude: data.longitude,
      timezone: data.timezone,
      profile: data.profile,
      isLastOwnerOfOrgs: data.isLastOwnerOfOrgs,
      isLastOwnerOfGroups: data.isLastOwnerOfGroups,
      hasEvents: data.hasEvents,
      canExportData: data.canExportData
    },
    savedListings: data.savedListings,
    savedSearches: data.savedSearches,
    authProviders: data.authProviders
  }));
}

export function getNotificationPreferences(): Promise<NotificationPreferences> {
  return getJson(`/data/userdashboard/notification-preferences`).then(
    ({ notificationPreferences }) => notificationPreferences
  );
}

export function updateNotificationPreferences(
  preferences: NotificationPreferences
): Promise<NotificationPreferences> {
  return putJson(
    `/data/userdashboard/notification-preferences`,
    preferences
  ).then(({ notificationPreferences }) => notificationPreferences);
}

export function getPaymentMethods(): Promise<Array<PaymentMethod>> {
  return getJson('/data/payment-methods').then(({ data }) => data);
}

export function addPaymentMethod(token: string): Promise<PaymentMethod> {
  return postJson(`/data/userdashboard/payment-methods`, {
    token
  }).then(({ paymentMethod }) => paymentMethod);
}

export function removePaymentMethod(methodId: string): Promise<void> {
  return del(`/data/userdashboard/payment-methods/${methodId}`);
}

export function fetchSavedListings(): Promise<Array<Object>> {
  return getJson('/data/userdashboard/saved-listings').then(
    ({ savedListings }) => savedListings
  );
}

export const fetchListingApplications = (params: { page: ?number }) => {
  const qs = queryString.stringify(params);
  return getJson(`/data/listing-applications?${qs}`).then(
    ({ listingApplications }) => listingApplications
  );
};

export const fetchApplicantProfile = (): Promise<ApplicantProfilePayload> =>
  getJson('/data/userdashboard/applicant-profile');

export const updateApplicantProfile = (
  values: ApplicantProfilePayload | { isRecruitable: boolean }
): Promise<ApplicantProfilePayload> =>
  putJson('/data/userdashboard/applicant-profile', values);
